<template>
    <div v-if="items.length" class="v-atlas-echarts-legend">
        <span v-for="item in items" :key="item.key ?? item.name" class="v-atlas-echarts-legend__item"
            :class="{ 'v-atlas-echarts-legend__item--inactive': !isSelected(item.key ?? item.name) }"
            :title="isSelected(item.key ?? item.name) ? 'Klicken zum Ausblenden' : 'Klicken zum Einblenden'"
            @click="emit('toggle', item.key ?? item.name)">
            <span class="v-atlas-echarts-legend__swatch" :style="{ backgroundColor: item.color }"></span>
            <span class="v-atlas-echarts-legend__label">{{ item.name }}</span>
        </span>
    </div>
</template>

<script setup lang="ts">
/**
 * A clickable HTML legend for an echarts chart.
 *
 * The echarts legend can have a variable number of entries with variable label
 * lengths, which makes it hard to size the canvas reliably. Instead of
 * rendering the legend inside the echarts canvas (where it can be clipped or
 * overlap the plot), this component renders it as a normal HTML element below
 * the chart. The chart height then only needs to fit the plot area, and the
 * legend can never be clipped or overlap.
 *
 * Clicking an item emits a `toggle` event with the item's stable key (falling
 * back to its name). The parent is responsible for dispatching the echarts
 * `legendToggleSelect` action and for keeping the `selected` prop in sync with
 * the echarts legend state.
 */

import { computed } from "vue"
import type { AtlasEChartsOption } from "atlas-shared"

export interface EChartsLegendItem {
    name: string
    color: string
    /** Stable, role-based series key (e.g. "kreis"). Falls back to `name`. */
    key?: string
}

const props = defineProps<{
    option?: AtlasEChartsOption
    selected?: Record<string, boolean>
}>()

const emit = defineEmits<{
    (e: "toggle", key: string): void
}>()

// Derive the legend items from the echarts option's legend data + series colors.
const items = computed<EChartsLegendItem[]>(() => {
    const data = props.option?.legend?.data ?? []
    const series = props.option?.series ?? []

    return data.map((name) => {
        const seriesEntry = series.find((s) => s.name === name)
        const color = seriesEntry?.lineStyle?.color ?? seriesEntry?.itemStyle?.color
        return { name, key: seriesEntry?.key, color: color ?? "#000" }
    })
})

function isSelected(key: string): boolean {
    return props.selected?.[key] ?? true
}
</script>

<style scoped>
.v-atlas-echarts-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.v-atlas-echarts-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 4px;
    transition: background-color 0.15s ease, opacity 0.15s ease;
    user-select: none;
}

.v-atlas-echarts-legend__item:hover {
    background-color: rgba(0, 0, 0, 0.06);
}

/* Active (selected) state: full color swatch + solid label */
.v-atlas-echarts-legend__item .v-atlas-echarts-legend__swatch {
    opacity: 1;
}

.v-atlas-echarts-legend__item .v-atlas-echarts-legend__label {
    opacity: 1;
    text-decoration: none;
}

/* Inactive (deselected) state: hollow swatch + dimmed/struck-through label */
.v-atlas-echarts-legend__item--inactive .v-atlas-echarts-legend__swatch {
    opacity: 0.35;
    box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.25);
}

.v-atlas-echarts-legend__item--inactive .v-atlas-echarts-legend__label {
    opacity: 0.45;
    text-decoration: line-through;
}

.v-atlas-echarts-legend__swatch {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    flex-shrink: 0;
    transition: opacity 0.15s ease;
}
</style>